// src/api/auth-api.js - Enhanced with account status checking
import { gql } from '@apollo/client';
import client from './apollo-client';

// GraphQL Queries and Mutations
const LOGIN_MUTATION = gql`
  mutation Login($loginInput: LoginInput!) {
    login(loginInput: $loginInput) {
      token
      user {
        id
        email
        firstName
        lastName
        userType
        isActive
        isPhoneVerified
        lastLogin
        createdAt
      }
    }
  }
`;

export const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($userId: String!, $currentPassword: String!, $newPassword: String!) {
    changePassword(userId: $userId, currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

// Query to check user status (for when user is already logged in)
const CHECK_USER_STATUS = gql`
  query CheckUserStatus($userId: ID!) {
    user(id: $userId) {
      id
      isActive
      blockReason
      userType
    }
  }
`;

// Auth API functions with enhanced account status checking
export const authApi = {
  login: async (email, password) => {
    try {
      const { data, errors } = await client.mutate({
        mutation: LOGIN_MUTATION,
        variables: { 
          loginInput: { email, password }
        }
      });
      
      if (errors) {
        console.error('GraphQL errors:', errors);
        
        // Handle specific error for inactive accounts
        const inactiveError = errors.find(error => 
          error.message.toLowerCase().includes('inactive') ||
          error.message.toLowerCase().includes('désactivé') ||
          error.message.toLowerCase().includes('deactivated')
        );
        
        if (inactiveError) {
          // Store minimal user info for the deactivated page
          const userData = { email };
          localStorage.setItem('deactivatedUserData', JSON.stringify(userData));
          throw new Error('ACCOUNT_DEACTIVATED');
        }
        
        throw new Error(errors[0].message);
      }
      
      if (data && data.login) {
        const { token, user } = data.login;
        
        // Check if account is active
        if (!user.isActive) {
          // Store user data for the deactivated page
          localStorage.setItem('deactivatedUserData', JSON.stringify(user));
          throw new Error('ACCOUNT_DEACTIVATED');
        }
        
        // Store token and user info for active accounts
        localStorage.setItem('authToken', token);
        localStorage.setItem('userType', user.userType);
        localStorage.setItem('userData', JSON.stringify(user));
        
        return data.login;
      }
      return null;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },
  
  // Check if current user account is still active
  checkAccountStatus: async () => {
    try {
      const userData = authApi.getUserData();
      if (!userData || !userData.id) {
        return { isActive: false, shouldRedirect: false };
      }

      const { data } = await client.query({
        query: CHECK_USER_STATUS,
        variables: { userId: userData.id },
        fetchPolicy: 'network-only' // Always check from server
      });

      if (data && data.user) {
        const isActive = data.user.isActive;

        if (!isActive) {
          // Account has been deactivated/blocked — store fresh data including blockReason
          const freshUserData = { ...userData, ...data.user };
          localStorage.setItem('deactivatedUserData', JSON.stringify(freshUserData));
          authApi.logout(); // Clear auth data
          return { isActive: false, shouldRedirect: true };
        }

        return { isActive: true, shouldRedirect: false };
      }

      // user(id) returned null — don't log out, let the session continue
      return { isActive: true, shouldRedirect: false };
    } catch (error) {
      console.error('Error checking account status:', error);
      return { isActive: false, shouldRedirect: false };
    }
  },
  
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('userData');
    // Keep deactivatedUserData if it exists for the deactivated page
  },
  
  // Enhanced authentication check
  isAuthenticated: () => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    return token !== null && userData !== null;
  },
  
  getUserType: () => {
    return localStorage.getItem('userType');
  },
  
  getUserData: () => {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  },
  
  // Get deactivated user data for the deactivated page
  getDeactivatedUserData: () => {
    const userData = localStorage.getItem('deactivatedUserData');
    return userData ? JSON.parse(userData) : null;
  },
  
  // Clear deactivated user data (after support ticket is sent)
  clearDeactivatedUserData: () => {
    localStorage.removeItem('deactivatedUserData');
  },
  
  // Check if user has deactivated account data
  hasDeactivatedAccount: () => {
    return localStorage.getItem('deactivatedUserData') !== null;
  }
};